import React from 'react';
import classNames from 'classnames/bind';
import { DateTime } from 'luxon';

import { TrackableStatusWidget as TrackableStatusWidgetType } from 'types/dashboard_widget';
import { WIDGET_REFETCH_INTERVAL } from 'dashboards/services/interval';

import { useDashboardFilterParams } from 'dashboards/hooks/useDashboardFilterParams';
import { useModal } from 'hooks/useModal';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useWatchlistStatus } from 'trackables/hooks';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { Heading } from 'components/Heading/Heading';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { Text } from 'components/Text/Text';
import { Thumbnail } from 'components/Thumbnail/Thumbnail';
import { Icon } from 'components/Icon/IconDeprecated';
import { Widget } from 'dashboards/widgets/Widget/Widget';
import { FilesWidgetSkeleton } from 'dashboards/widgets/Files/Skeleton';

import { resolveTrackableSourceDetails } from 'trackables/utils';

import styles from './Trackables.module.scss';

const c = classNames.bind(styles);

export function TrackableStatusWidget(widget: TrackableStatusWidgetType) {
  const { options } = widget;

  const trackableListIds = React.useMemo(
    () => options?.trackable_list_ids ?? [],
    [options]
  );
  const selectedTrackableItemIds =
    options.trackable_item_ids?.filter(Boolean) ?? [];

  const { start, stop, timezone } = useTimeRangeParams();
  const { gateway_id } = useDashboardFilterParams();
  const { open } = useModal();

  const {
    trackableItems,
    matchesByTrackableId,
    streamsMap,
    camerasMap,
    filesMap,
    isLoading,
    shouldFetchItems,
  } = useWatchlistStatus({
    watchlistIds: trackableListIds,
    watchlistItemIds: selectedTrackableItemIds,
    timeRangeStart: start,
    timeRangeStop: stop,
    gatewayIds: gateway_id ? [gateway_id] : undefined,
    refetchInterval: WIDGET_REFETCH_INTERVAL,
  });

  let content: React.ReactNode = null;
  if (!shouldFetchItems) {
    content = (
      <EmptyView>
        Select at least one trackable list to monitor the latest matches.
      </EmptyView>
    );
  } else if (isLoading) {
    content = (
      <SkeletonList
        min={trackableItems.length || 5}
        max={trackableItems.length || 5}
        component={<FilesWidgetSkeleton />}
      />
    );
  } else if (trackableItems.length === 0) {
    content = <EmptyView>No watchlist items in the selected lists.</EmptyView>;
  } else {
    content = (
      <ul className={c('trackable-list')}>
        {trackableItems.map((item) => {
          const match = matchesByTrackableId.get(item.id);
          const handleClick = match
            ? () =>
                open('files-search', {
                  defaultFile: {
                    id: match.file_id,
                    created_at: match.created_at,
                  },
                  defaultStreamId: match.stream_id,
                })
            : undefined;

          const { sourceLabel, sourceIcon, fileLabel } =
            resolveTrackableSourceDetails(match, {
              cameras: camerasMap,
              streams: streamsMap,
              files: filesMap,
            });
          const showSource = sourceLabel && sourceIcon !== 'file';
          const lastMatchLabel = match
            ? DateTime.fromISO(match.created_at)
                .setZone(timezone)
                .toLocaleString(DateTime.DATETIME_MED)
            : null;

          return (
            <li key={item.id}>
              <button
                className={c('trackable-row', {
                  disabled: !match,
                })}
                type="button"
                onClick={handleClick}
                disabled={!match}
              >
                {match ? (
                  <Thumbnail
                    className={c('trackable-thumbnail')}
                    mime_type={match.mime_type}
                    thumbnail_url={match.thumbnail_url}
                  />
                ) : (
                  <Thumbnail className={c('trackable-thumbnail')} isLoading />
                )}
                <div className={c('trackable-info')}>
                  <Heading level="4" className={c('trackable-heading')}>
                    {item.name}
                  </Heading>
                  {match ? (
                    <>
                      {lastMatchLabel && (
                        <Text size="small">
                          {`Last match ${lastMatchLabel}`}
                        </Text>
                      )}
                      {fileLabel && (
                        <Text className={c('trackable-name')}>{fileLabel}</Text>
                      )}
                      {showSource && (
                        <Text className={c('trackable-source')}>
                          {sourceIcon && <Icon name={sourceIcon} />}
                          <span>{sourceLabel}</span>
                        </Text>
                      )}
                    </>
                  ) : (
                    <Text size="small">No matches in selected time range</Text>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Widget widget={widget}>
      <div className={c('trackable-widget')}>{content}</div>
    </Widget>
  );
}
